import React, { useState } from "react";
import { useProjectOverview } from "./CopilotProjectOverview";

/* The project's summary, kept at the top of the conversation.
 *
 * The opening overview is the empty screen, so it goes the moment the first
 * message is sent — and with it the one thing people look back at while they ask
 * questions: what this project actually is. This stays, as the first thing in the
 * thread, from the first message to the last.
 *
 * Open the first time, because a summary nobody sees is not a summary; after
 * that it follows the reader — collapsed to a single line if they fold it away,
 * and remembered per project, so folding it once does not mean folding it every
 * time the chat is reopened.
 *
 * It shows nothing until there is something true to show: no card while the
 * documents are still being read, none for a project without a summary.
 */

const OPEN_KEY = (projectId) => `rfp_copilot_summary_open_${projectId}`;
const readOpen = (projectId) => {
  try {
    return localStorage.getItem(OPEN_KEY(projectId)) !== "0";
  } catch {
    return true;
  }
};
const writeOpen = (projectId, open) => {
  try {
    localStorage.setItem(OPEN_KEY(projectId), open ? "1" : "0");
  } catch {
    /* storage unavailable: it opens again next time, which is the safe default */
  }
};

const ICON = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };

const CopilotProjectSummary = ({ projectId, projectName, onOpenDocuments }) => {
  const { data } = useProjectOverview(projectId);
  const [open, setOpen] = useState(() => readOpen(projectId));

  // Another project, another remembered state.
  const [seen, setSeen] = useState(projectId);
  if (seen !== projectId) {
    setSeen(projectId);
    setOpen(readOpen(projectId));
  }

  if (!data?.ready || !data.summary) return null;

  const name = data.project_name || projectName || "Project";
  const paragraphs = data.summary.split(/\n{2,}/).filter(Boolean);
  const toggle = () => {
    setOpen((v) => {
      writeOpen(projectId, !v);
      return !v;
    });
  };

  return (
    <section
      aria-label="Project summary"
      className="rounded-2xl border border-[#A04A97]/15 bg-white/85 shadow-[0_1px_2px_rgba(24,16,28,0.04),0_14px_30px_-26px_rgba(24,16,28,0.5)]"
    >
      <div className="flex items-center gap-2 px-4 py-3">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2"
        >
          <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-[#A04A97]/10 text-[#A04A97]">
            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" {...ICON}>
              <path d="M12 3l1.9 4.6L18.5 9l-4.6 1.9L12 15.5l-1.9-4.6L5.5 9l4.6-1.4z" />
            </svg>
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[11.5px] font-semibold uppercase tracking-[0.09em] text-[#77707E]">
              Project summary
            </span>
            <span className="block truncate text-[14.5px] font-semibold text-[#18101C]" title={name}>
              {name}
            </span>
          </span>
          <svg
            aria-hidden="true"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            className={`flex-none text-[#6B6472] transition-transform duration-200 motion-reduce:transition-none ${
              open ? "rotate-180" : ""
            }`}
            {...ICON}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {onOpenDocuments && data.document_count > 0 && (
          <button
            type="button"
            onClick={onOpenDocuments}
            title="Open the documents beside the chat"
            className="hidden flex-none items-center gap-1.5 rounded-full border border-[#18101C]/12 bg-white px-2.5 py-1 text-[12px] font-semibold text-[#42404A] transition-colors hover:border-[#A04A97]/40 hover:text-[#8B3F83] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] sm:inline-flex"
          >
            {data.document_count} document{data.document_count === 1 ? "" : "s"}
          </button>
        )}
      </div>

      {open && (
        <div className="border-t border-[#18101C]/[0.06] px-4 pb-4 pt-3">
          {data.headline && (
            <p className="text-[15.5px] font-medium leading-snug text-[#2B2733]">{data.headline}</p>
          )}
          <div className="mt-2 space-y-2.5 break-words text-[14.5px] leading-[1.65] text-[#3F3B45]">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          {data.facts?.length > 0 && (
            <dl className="mt-3.5 flex flex-wrap gap-2">
              {data.facts.map((f) => (
                <div
                  key={f.label}
                  className="min-w-0 max-w-full rounded-lg border border-[#18101C]/[0.07] bg-white px-3 py-1.5"
                >
                  <dt className="text-[10.5px] font-semibold uppercase tracking-[0.09em] text-[#77707E]">{f.label}</dt>
                  <dd className="break-words text-[13.5px] font-medium leading-snug text-[#18101C]">{f.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}
    </section>
  );
};

export default CopilotProjectSummary;
